// RiskAssessmentsMantine.js
// Fixed version with working tabs and proper styling

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { authFetch } from './utils/authFetch';
import StartAssessmentDialogV2 from './StartAssessmentDialogV2';
import ReviewMode from './review/ReviewMode';
import ReviewStatusBadge from './components/ReviewStatusBadge';
import DeleteAssessmentModal from './components/DeleteAssessmentModal';
import {

  Title,
  Table,
  Group,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  Chip,
  TextInput,
  Select,
  RingProgress,
  Stack,
  Card,
  Center,
  Avatar,
  ThemeIcon,
  Container,
  Button,
  Divider
} from '@mantine/core';
import {
  IconSearch,
  IconPlayerPlay,
  IconProgressCheck,
  IconAlertCircle,
  IconUser,
  IconFileAnalytics,
  IconClipboardCheck,
  IconRocket,
  IconEye,
  IconTool,
  IconCompass,
  IconTrash,
  IconRestore
} from '@tabler/icons-react';
import AIGeneratingIcon from './components/AIGeneratingIcon';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';
import { isRiskManager } from './utils/roleHelpers';
import { selectAllAssessments } from './redux/selectors/assessmentSelectors';
import { selectModelsById } from './redux/slices/modelsSlice';
import { setCurrentAssessment, setCurrentModel } from './redux/slices/appSlice';
import { deleteAssessment, restoreAssessment, fetchAllAssessments } from './redux/slices/assessmentsSlice';

// Priority colors for risk badges (consistent with canvas components)
const PRIORITY_COLORS = {
  Critical: { bg: '#1A1A1A', text: '#fff' },
  High: { bg: '#993333', text: '#fff' },
  Significant: { bg: '#B94D4D', text: '#fff' },
  Moderate: { bg: '#D4AA7D', text: '#333' },
  Low: { bg: '#B8C2B7', text: '#333' },
  Negligible: { bg: '#D8D8D8', text: '#333' }
};

const RiskAssessmentsMantine = () => {
  // NEW: Use assessment-centric Redux structure
  const allAssessmentsById = useSelector(selectAllAssessments);
  const assessmentsLoading = useSelector((state) => state.assessments?.loading?.all || false);

  // Models from RTK slice - used to calculate available models for new assessments
  const modelsById = useSelector(selectModelsById);

  // Risks from RTK slice - to show risk counts on assessment cards
  const risksByAssessment = useSelector((state) => state.riskItems?.byAssessment || {});

  // LEGACY: Keep reviews data from old structure for backward compatibility
  const reviewsData = useSelector((state) => state.reviews.byModel || {});

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // Will be set by smart default logic
  const [userHasSelectedTab, setUserHasSelectedTab] = useState(false); // Track manual selection
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_at'); // Default to most recent
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'builder' | 'use_case' | 'business_case'
  const [showModeDialog, setShowModeDialog] = useState(false);
  const [selectedModelForAssessment, setSelectedModelForAssessment] = useState(null);
  const [userIsRiskManager, setUserIsRiskManager] = useState(false);
  const [selectedAssessmentForReview, setSelectedAssessmentForReview] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [deletedAssessments, setDeletedAssessments] = useState([]);

  // Note: Assessments are fetched in App.js during initialization
  // No need to fetch again here - data comes from Redux state

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            scope: 'openid profile email read:models write:models delete:models'
          }
        });

        const decoded = jwtDecode(token);
        const roles = decoded['https://agenthuginn.ai/claims/roles'] || decoded['https://riskgpt.io/claims/roles'] || [];
        const isRM = isRiskManager(roles);
        setUserIsRiskManager(isRM);
        setCurrentUserId(decoded.sub); // Store user ID for ownership checks
      } catch (error) {
        console.error('[RiskAssessmentsMantine] Error fetching roles:', error);
        setUserIsRiskManager(false);
      }
    };

    if (isAuthenticated) {
      fetchRoles();
    }
  }, [getAccessTokenSilently, isAuthenticated]);


  // Helper to calculate risk statistics for an assessment
  const calculateRiskStats = useCallback((assessmentUuid, assessmentType) => {
    const risks = risksByAssessment[assessmentUuid];
    if (!risks) return { totalRisks: 0, priorities: {}, totalActions: 0 };

    const priorities = { Critical: 0, High: 0, Significant: 0, Moderate: 0, Low: 0, Negligible: 0 };
    let totalRisks = 0;
    let totalActions = 0;

    if ((assessmentType === 'explorer' || assessmentType === 'business_case' || assessmentType === 'use_case') && risks.explorerRisks) {
      // Explorer assessment: count risks from explorerRisks
      Object.values(risks.explorerRisks).forEach(area => {
        const areaRisks = area?.areaRisks || {};
        Object.values(areaRisks).forEach(categoryRisks => {
          if (Array.isArray(categoryRisks)) {
            categoryRisks.forEach(risk => {
              totalRisks++;
              const priority = risk.priority || 'Moderate';
              // Normalize priority to title case
              const normalizedPriority = priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
              if (priorities[normalizedPriority] !== undefined) {
                priorities[normalizedPriority]++;
              }
              totalActions += (risk.controls?.length || 0);
            });
          }
        });
      });
    } else if (risks.modelRisks) {
      // Builder assessment: count risks from modelRisks
      Object.values(risks.modelRisks).forEach(sectionRisks => {
        const areaRisks = sectionRisks?.areaRisks || {};
        Object.values(areaRisks).forEach(categoryRisks => {
          if (Array.isArray(categoryRisks)) {
            categoryRisks.forEach(risk => {
              if (risk.status === 'Saved') {
                totalRisks++;
                const priority = risk.priority || risk.evaluation?.priority || 'Moderate';
                const normalizedPriority = priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
                if (priorities[normalizedPriority] !== undefined) {
                  priorities[normalizedPriority]++;
                }
                totalActions += (risk.controls?.length || 0);
              }
            });
          }
        });
      });
    }

    return { totalRisks, priorities, totalActions };
  }, [risksByAssessment]);

  // Normalize status helper
  const normalizeStatus = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'not started':
        return 'Not Started';
      case 'on-going':
      case 'ongoing':
        return 'On-Going';
      case 'completed':
        return 'Completed';
      default:
        return 'Not Started';
    }
  }, []);

  // Enhanced progress calculation
  const calculateProgress = useCallback((model) => {
    // If assessment is completed, always return 100%
    const status = normalizeStatus(model.assessmentStatus);
    if (status === 'Completed') {
      return 100;
    }

    // If no position data, return 0 (not started)
    if (!model.position) return 0;

    const currentStep = model.position.majorStep;

    // Autopilot mode uses different step names
    // Map autopilot steps: upload → context → risks → review → completed
    const autopilotStepWeights = {
      upload: 10,
      context: 25,
      risks: 70,
      review: 90,
      completed: 100
    };

    // Check if this is autopilot flow (has autopilot-specific steps)
    if (autopilotStepWeights[currentStep] !== undefined) {
      let progress = autopilotStepWeights[currentStep];
      // Add partial progress within risks step based on narrativesApproved
      if (currentStep === 'risks' && model.position.narrativesApproved) {
        progress = 50; // Halfway through risks
      }
      // stepCompleted indicates the previous step is done
      if (model.position.stepCompleted === 'risks') {
        progress = 90; // Risks done, in review
      }
      return Math.min(progress, 100);
    }

    // Copilot mode uses original step names
    const stepWeights = {
      useCase: 20,
      safetyRisks: 50,
      securityAssessment: 20,
      compliance: 5,
      assessmentSummary: 5
    };

    const stepOrder = ['useCase', 'safetyRisks', 'securityAssessment', 'compliance', 'assessmentSummary'];
    const currentIndex = stepOrder.indexOf(currentStep);

    let progress = 0;
    for (let i = 0; i < currentIndex; i++) {
      progress += stepWeights[stepOrder[i]];
    }

    if (currentStep === 'safetyRisks' && model.position.section) {
      progress += stepWeights[currentStep] * 0.3;
    }

    return Math.min(progress, 100);
  }, [normalizeStatus]);

  // NEW: Transform assessment-centric data to model-centric format for backward compatibility
  const allModels = useMemo(() => {
    const modelsObj = {};

    Object.entries(allAssessmentsById).forEach(([assessmentUuid, assessment]) => {
      // Skip deleted assessments - they should only appear in "Deleted" tab
      if (assessment.deleted_at) return;

      // Hide retired Builder rows (assessment_type='builder') from user-facing lists.
      // They live under Knowledge Base → Model Risk Register as catalogue entries.
      if (assessment.assessment_type === 'builder') return;

      // Use model_uuid as key, or assessment_uuid if no model (corporate edition)
      const modelId = assessment.model_uuid || assessmentUuid;

      // Assessment-centric naming: prioritize assessment_name from info object
      // For Explorer assessments, ensure we use the Explorer's own assessment_name, not parent's
      const assessmentType = assessment.assessment_type || 'builder';
      const assessmentName = assessment.info?.assessment_name ||
                            assessment.assessment_name ||
                            (assessmentType === 'explorer' ? `Explorer: ${assessment.model_name}` : assessment.model_name) ||
                            'Unnamed Assessment';

      // Owner fallback chain: owner object -> direct fields -> info fields
      const ownerName = assessment.owner?.name || assessment.owner_name || assessment.info?.created_by_name;
      const ownerEmail = assessment.owner?.email || assessment.owner_email;
      // Avatar: use info.avatar (from creator), could add owner avatar if stored
      const userAvatar = assessment.info?.avatar || assessment.owner?.avatar;

      modelsObj[modelId] = {
        // Model identification
        modelId: modelId,
        modelName: assessmentName, // Now uses assessment name as primary
        model_name: assessment.model_name, // Keep original model_name for reference
        assessmentName: assessmentName, // Explicit assessment name field
        modelType: assessment.model_type,
        modelStage: assessment.model_stage,
        useCaseShortDescription: assessment.use_case_short_description,

        // Assessment data
        assessmentUuid: assessmentUuid,
        assessmentStatus: assessment.info?.status || 'Not Started',
        position: assessment.position,
        step: assessment.current_step,
        mode: assessment.info?.mode,
        template_id: assessment.info?.template_id,
        // Use backend-calculated progress if available
        backendProgress: assessment.info?.progress,

        // Review data
        review_status: assessment.review_status,
        review_started_by: assessment.review_started_by,
        review_started_by_name: assessment.review_started_by_name,
        review_started_at: assessment.review_started_at,
        reviewed_by: assessment.reviewed_by,
        reviewed_by_name: assessment.reviewed_by_name,
        reviewed_at: assessment.reviewed_at,

        // Activity and ownership tracking
        created_at: assessment.info?.created_at,
        created_by: assessment.info?.created_by,
        created_by_name: assessment.info?.created_by_name,
        avatar: userAvatar,
        updated_at: assessment.info?.updated_at,
        last_updated_by: assessment.info?.last_updated_by,
        last_updated_by_name: assessment.info?.last_updated_by_name,
        last_updated_by_email: assessment.info?.last_updated_by_email,
        last_activity_at: assessment.info?.last_activity_at,
        last_activity_by_name: assessment.info?.last_activity_by_name,
        last_activity_by_email: assessment.info?.last_activity_by_email,

        // Owner fields - use fallback chain
        owner_id: assessment.owner?.id || assessment.owner_id,
        owner_name: ownerName,
        owner_email: ownerEmail,
        createdByName: ownerName,
        // Assessment type: builder or explorer
        assessmentType: assessment.assessment_type || 'builder',
        // Risk statistics (calculated from Redux state)
        riskStats: calculateRiskStats(assessmentUuid, assessment.assessment_type || 'builder'),
      };
    });

    return modelsObj;
  }, [allAssessmentsById, calculateRiskStats]);

  // Enhanced models array with computed properties
  const modelsArray = useMemo(() => {
    const arr = Object.entries(allModels).map(([modelId, model]) => {
      // Get review status from assessment data (primary source)
      // Legacy fallback for backward compatibility during transition
      const reviewStatus = model.review_status || reviewsData[modelId]?.reviewStatus || null;

      // Get user display name with comprehensive fallback chain
      const userName = model.last_updated_by_name ||
                       model.last_activity_by_name ||
                       model.owner_name ||
                       model.createdByName ||
                       model.created_by_name;
      const userEmail = model.last_updated_by_email ||
                        model.last_activity_by_email ||
                        model.owner_email ||
                        model.created_by;
      const displayName = userName || (userEmail ? userEmail.split('@')[0] : 'Unknown User');

      // Use backend progress if available AND > 0, otherwise calculate from position
      // Note: Backend currently stores 0 for all, so we always calculate until that's fixed
      const progress = model.backendProgress > 0
        ? model.backendProgress
        : calculateProgress(model);

      return {
        ...model,
        modelId,
        assessmentStatus: normalizeStatus(model.assessmentStatus),
        currentStep: model.step,
        position: model.position,
        progress,
        lastActivity: model.updated_at || model.last_activity_at || model.created_at,
        lastActivityBy: displayName,
        review_status: reviewStatus // Use review status from reviews Redux state
      };
    });

    // Sort by selected criteria
    arr.sort((a, b) => {
      switch (sortBy) {
        case 'updated_at':
          return new Date(b.lastActivity || b.updated_at || 0) - new Date(a.lastActivity || a.updated_at || 0);
        case 'name':
          return (a.assessmentName || a.modelName || '').localeCompare(b.assessmentName || b.modelName || '');
        case 'progress':
          return b.progress - a.progress;
        case 'type':
          return (a.assessmentType || 'builder').localeCompare(b.assessmentType || 'builder');
        case 'created_at':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    return arr;
  }, [allModels, reviewsData, sortBy, calculateProgress, normalizeStatus]);

  // Status counts for assessments
  const statusCounts = useMemo(() => {
    // Get model_uuids that already have assessments
    const modelsWithAssessments = new Set(
      Object.values(allAssessmentsById).map(a => a.model_uuid).filter(Boolean)
    );

    // Count models WITHOUT any assessment (available for new assessments)
    const modelsAvailable = Object.keys(modelsById).filter(
      modelId => !modelsWithAssessments.has(modelId)
    ).length;

    return {
      all: modelsArray.length,
      ongoing: modelsArray.filter(m => m.assessmentStatus === 'On-Going').length,
      notstarted: modelsArray.filter(m => m.assessmentStatus === 'Not Started').length,
      completed: modelsArray.filter(m => m.assessmentStatus === 'Completed').length,
      forreview: modelsArray.filter(m => m.review_status === 'submitted').length,
      inreview: modelsArray.filter(m => m.review_status === 'submitted').length,
      reviewcompleted: modelsArray.filter(m => m.review_status === 'approved').length,
      deleted: deletedAssessments.length, // Count of deleted assessments
      // NEW: Models available for new assessments (no existing assessment)
      modelsAvailable,
      // Type counts for filter badges
      builder: modelsArray.filter(m => m.assessmentType === 'builder' || !m.assessmentType).length,
      use_case: modelsArray.filter(m => m.assessmentType === 'use_case').length,
      business_case: modelsArray.filter(m => m.assessmentType === 'business_case' || m.assessmentType === 'explorer').length,
    };
  }, [modelsArray, modelsById, allAssessmentsById, deletedAssessments]);

  // Smart default tab logic
  const getDefaultTab = useMemo(() => {
    if (userIsRiskManager && statusCounts.forreview > 0) return 'forreview';
    if (!userIsRiskManager && statusCounts.inreview > 0) return 'inreview';
    if (statusCounts.ongoing > 0) return 'ongoing';
    return 'all';
  }, [statusCounts, userIsRiskManager]);

  // Enhanced filtering
  const filteredModels = useMemo(() => {
    // If 'deleted' tab is active, show deleted assessments instead of modelsArray
    if (activeTab === 'deleted') {
      let filtered = deletedAssessments;

      // Filter by type (Builder/Explorer)
      if (typeFilter === 'builder') {
        filtered = filtered.filter(m => m.assessmentType === 'builder' || !m.assessmentType);
      } else if (typeFilter === 'use_case') {
        filtered = filtered.filter(m => m.assessmentType === 'use_case');
      } else if (typeFilter === 'business_case') {
        filtered = filtered.filter(m => m.assessmentType === 'business_case' || m.assessmentType === 'explorer');
      }

      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(model =>
          (model.modelName || model.model_name || '').toLowerCase().includes(query) ||
          model.assessmentStatus.toLowerCase().includes(query) ||
          (model.step && model.step.toLowerCase().includes(query))
        );
      }

      return filtered;
    }

    // Normal filtering for non-deleted assessments
    let filtered = modelsArray;

    // Filter by type (Builder/Builder/Explorer)
    if (typeFilter === 'builder') {
      filtered = filtered.filter(m => m.assessmentType === 'builder' || !m.assessmentType);
    } else if (typeFilter === 'use_case') {
      filtered = filtered.filter(m => m.assessmentType === 'use_case');
    } else if (typeFilter === 'business_case') {
      filtered = filtered.filter(m => m.assessmentType === 'business_case' || m.assessmentType === 'explorer');
    }

    // Filter by active tab
    if (activeTab !== 'all') {
      const statusMap = {
        'ongoing': 'On-Going',
        'notstarted': 'Not Started',
        'completed': 'Completed'
      };
      const mappedStatus = statusMap[activeTab];

      // Handle review status tabs separately
      if (activeTab === 'forreview' || activeTab === 'inreview') {
        filtered = filtered.filter(model => model.review_status === 'submitted');
      } else if (activeTab === 'reviewcompleted') {
        filtered = filtered.filter(model => model.review_status === 'approved');
      } else if (mappedStatus) {
        filtered = filtered.filter(model => model.assessmentStatus === mappedStatus);
      }
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(model =>
        (model.modelName || model.model_name || '').toLowerCase().includes(query) ||
        model.assessmentStatus.toLowerCase().includes(query) ||
        (model.step && model.step.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [modelsArray, activeTab, searchQuery, typeFilter, deletedAssessments]);

  // Assessment type badge component - enhanced for visual distinction
  const TypeBadge = ({ type }) => {
    const typeConfig = {
      builder: {
        color: 'blue',
        icon: IconTool,
        label: 'Register Builder',
        variant: 'light'
      },
      use_case: {
        color: 'orange',
        icon: IconTool,
        label: 'Builder',
        variant: 'light'
      },
      business_case: {
        color: 'green',
        icon: IconCompass,
        label: 'Explorer',
        variant: 'filled'
      },
      // Legacy support
      explorer: {
        color: 'green',
        icon: IconCompass,
        label: 'Explorer',
        variant: 'filled'
      }
    };
    const config = typeConfig[type] || typeConfig.builder;
    const Icon = config.icon;

    return (
      <Badge
        variant={config.variant}
        color={config.color}
        size="sm"
        leftSection={<Icon size={12} />}
        radius="md"
        styles={{
          root: {
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }
        }}
      >
        {config.label}
      </Badge>
    );
  };

  // Compact risk summary component showing priority distribution
  const RiskSummary = ({ riskStats }) => {
    if (!riskStats || riskStats.totalRisks === 0) {
      return <Text size="xs" c="dimmed">No risks</Text>;
    }

    const { priorities, totalRisks } = riskStats;
    const priorityOrder = ['Critical', 'High', 'Significant', 'Moderate', 'Low'];

    // Show compact badges for top 3 non-zero priorities
    const activePriorities = priorityOrder
      .filter(p => priorities[p] > 0)
      .slice(0, 3);

    return (
      <Tooltip
        label={
          <Stack gap={4}>
            <Text size="xs" fw={600}>{totalRisks} Total Risks</Text>
            {priorityOrder.map(p => priorities[p] > 0 && (
              <Group key={p} gap={8}>
                <Text size="xs">{p}:</Text>
                <Text size="xs" fw={600}>{priorities[p]}</Text>
              </Group>
            ))}
          </Stack>
        }
        withArrow
        position="top"
      >
        <Group gap={4}>
          {activePriorities.map(priority => (
            <Badge
              key={priority}
              size="xs"
              variant="filled"
              radius="sm"
              style={{
                backgroundColor: PRIORITY_COLORS[priority]?.bg || '#888',
                color: PRIORITY_COLORS[priority]?.text || '#fff',
                minWidth: 20,
                paddingLeft: 4,
                paddingRight: 4
              }}
            >
              {priorities[priority]}
            </Badge>
          ))}
          <Text size="xs" c="dimmed">({totalRisks})</Text>
        </Group>
      </Tooltip>
    );
  };

  // Enhanced progress ring component
  const ProgressRing = ({ progress, size = 50 }) => {
    const color = progress === 100 ? 'green' : progress > 50 ? 'blue' : progress > 25 ? 'yellow' : 'red';

    return (
      <RingProgress
        size={size}
        thickness={4}
        roundCaps
        sections={[{ value: progress, color }]}
        label={
          <Text
            c={color}
            fw={700}
            ta="center"
            size="10px"
            style={{ lineHeight: '1' }}
          >
            {Math.round(progress)}%
          </Text>
        }
      />
    );
  };

  // Time since last activity
  const getTimeSinceActivity = (lastActivity) => {
    if (!lastActivity) return 'Unknown';
    
    const now = new Date();
    const last = new Date(lastActivity);
    const diffMs = now - last;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  // Utility to convert camelCase or snake_case to Title Case
  const toTitleCase = (str) => {
    if (!str) return '';
    return str
      // Handle camelCase: insert space before capital letters
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Handle snake_case: replace underscores with spaces
      .replace(/_/g, ' ')
      // Capitalize first letter of each word
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Position label helper
  const getPositionLabel = (position, step) => {
    if (!position && !step) return 'Not Started';

    const stepLabels = {
      useCase: 'Context',
      contextAssessment: 'Context',
      safetyRisks: 'Safety Risks',
      securityAssessment: 'Security',
      compliance: 'Compliance',
      assessmentSummary: 'Summary'
    };

    const phaseLabels = {
      discovery: 'Discovery',
      evaluation: 'Evaluation',
      actions: 'Actions',
      residual: 'Residual',
      security: 'Security',
      completed: 'Complete'
    };

    if (position) {
      // Autopilot position structure: majorStep='risks', currentArea, phase
      if (position.majorStep === 'risks' && position.currentArea) {
        const areaLabel = toTitleCase(position.currentArea);
        const phaseLabel = phaseLabels[position.phase] || toTitleCase(position.phase);
        return phaseLabel ? `${areaLabel} - ${phaseLabel}` : areaLabel;
      }

      // Copilot position structure: majorStep, section
      const stepLabel = stepLabels[position.majorStep] || position.majorStep;
      if (position.section) {
        return `${stepLabel}`;
      }
      return stepLabel;
    }

    // Fallback to step if position not available
    if (step) {
      return stepLabels[step] || step.replace(/([A-Z])/g, ' $1').trim();
    }

    return 'Not Started';
  };

  // Determine if assessment action button (start/resume) should be shown to current user.
  // Co-pilot mode has been removed; all flows are autopilot. Legacy rows without a mode are
  // restricted to risk managers (admin Register Builder surface).
  const canShowActionButton = (model) => {
    if (model.mode === 'autopilot') return true;
    return userIsRiskManager;
  };

  // Get button configuration (label, tooltip, color) based on assessment state
  const getActionButtonConfig = (model) => {
    const isAutopilot = model.mode === 'autopilot';
    const isOngoing = model.assessmentStatus === 'On-Going';

    // For autopilot, check if documents were uploaded
    // Position majorStep 'upload' or null means docs not uploaded yet
    const hasUploadedDocs = model.position?.majorStep && model.position.majorStep !== 'upload';

    if (isAutopilot && !hasUploadedDocs) {
      return {
        label: 'Start Assessment',
        tooltip: 'Upload model card to begin assessment',
        color: 'blue'
      };
    }

    if (isOngoing) {
      return {
        label: 'Resume Assessment',
        tooltip: 'Continue from where you left off',
        color: 'green'
      };
    }

    return {
      label: 'Start Assessment',
      tooltip: 'Begin new assessment',
      color: 'blue'
    };
  };

  // Handle model selection - now shows mode selection dialog
  const handleModelSelection = (model) => {
    // If it's a Not Started assessment, show mode selection dialog
    if (model.assessmentStatus === 'Not Started' || !model.assessmentStatus) {
      setSelectedModelForAssessment(model);
      setShowModeDialog(true);
      return;
    }

    // Completed assessments: Navigate to report/canvas view
    if (model.assessmentStatus === 'Completed') {
      console.log('[handleModelSelection] ✅ Navigating to completed assessment report');
      // Route based on assessment type
      if (model.assessmentType === 'use_case') {
        navigate(`/builder-report/${model.modelId}/${model.assessmentUuid}`);
      } else if (model.assessmentType === 'business_case' || model.assessmentType === 'explorer') {
        navigate(`/explorer-report/${model.modelId}/${model.assessmentUuid}`);
      } else {
        navigate(`/assessment-report/${model.modelId}`, {
          state: {
            assessmentUuid: model.assessmentUuid,
            assessmentName: model.assessmentName || model.assessment_name
          }
        });
      }
      return;
    }

    // For ongoing assessments, resume in the correct mode (autopilot or co-pilot)
    resumeExistingAssessment(model);
  };

  // Resume existing ongoing assessment - navigates based on assessment mode
  // IMPORTANT: Co-pilot mode only accessible by Risk Managers
  // Autopilot mode never falls back to co-pilot
  const resumeExistingAssessment = async (model) => {
    console.log('═══════════════════════════════════════════════════════');
    console.log('[resumeExistingAssessment] 🔄 RESUMING ASSESSMENT');
    console.log('[resumeExistingAssessment] Model ID:', model.modelId);
    console.log('[resumeExistingAssessment] Mode:', model.mode, '| User is Risk Manager:', userIsRiskManager);
    console.log('═══════════════════════════════════════════════════════');

    // Set current context in RTK state
    dispatch(setCurrentModel(model.modelId));
    if (model.assessmentUuid) {
      dispatch(setCurrentAssessment(model.assessmentUuid));
    }

    // Builder assessments: always use navigator flow
    if (model.assessmentType === 'use_case') {
      const url = `/builder-assessment/${model.modelId}/${model.assessmentUuid}`;
      console.log('[resumeExistingAssessment] ✅ Navigating to BUILDER:', url);
      navigate(url);
      return;
    }

    // Explorer/business_case assessments: always use explorer flow
    if (model.assessmentType === 'business_case' || model.assessmentType === 'explorer') {
      const url = `/explorer-assessment/${model.modelId}/${model.assessmentUuid}`;
      console.log('[resumeExistingAssessment] ✅ Navigating to EXPLORER:', url);
      navigate(url);
      return;
    }

    // All remaining flows resume on the autopilot route (legacy 'builder' rows live here too,
    // accessible only to risk managers per canShowActionButton).
    if (model.assessmentUuid) {
      const url = `/autopilot-assessment/${model.assessmentUuid}`;
      console.log('[resumeExistingAssessment] ✅ Navigating to AUTOPILOT:', url);
      navigate(url);
      return;
    }

    // Fallback: fetch from database if assessmentUuid is missing
    console.log('[resumeExistingAssessment] ⚠️ No assessmentUuid in Redux, fetching from database...');

    try {
      const response = await authFetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        body: JSON.stringify({ modelId: model.modelId, operation: 'fetch' }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const result = await response.json();
        const assessmentUuid = result.assessmentUuid;
        dispatch(setCurrentAssessment(assessmentUuid));

        const url = `/autopilot-assessment/${assessmentUuid}`;
        console.log('[resumeExistingAssessment] ✅ Navigating to AUTOPILOT (from DB):', url);
        navigate(url);
      } else {
        console.error('[resumeExistingAssessment] ❌ Failed to fetch existing assessment');
      }
    } catch (error) {
      console.error('[resumeExistingAssessment] ❌ Error resuming assessment:', error);
    }
  };

  // Handle delete button click - opens modal with assessment details
  const handleDeleteClick = (model) => {
    // Transform model to match modal's expected field names
    const assessmentForModal = {
      assessment_uuid: model.assessmentUuid,
      assessment_name: model.assessmentName || model.assessment_name,
      assessment_type: model.assessmentType || model.assessment_type,
      owner_name: model.owner_name || model.owner?.name
    };
    setAssessmentToDelete(assessmentForModal);
    setDeleteModalOpen(true);
  };

  // Handle delete confirmation from modal
  const handleDeleteConfirm = async (deleteParams) => {
    try {
      await dispatch(deleteAssessment(deleteParams)).unwrap();
      // Refresh assessments list
      await dispatch(fetchAllAssessments());
      setDeleteModalOpen(false);
      setAssessmentToDelete(null);
    } catch (error) {
      console.error('[RiskAssessmentsMantine] Delete failed:', error);
      // Modal will stay open to show error
    }
  };

  // Handle restore button click
  const handleRestoreClick = async (assessmentUuid) => {
    try {
      await dispatch(restoreAssessment({ assessmentUuid })).unwrap();
      // Refresh active assessments (without deleted ones)
      await dispatch(fetchAllAssessments());

      // Re-fetch deleted assessments directly from API without updating Redux
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
          scope: 'openid profile email read:models write:models delete:models'
        }
      });

      const response = await authFetch('/.netlify/functions/getAssessments?includeDeleted=true', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      const deleted = result.assessments.filter(a => a.deleted_at);

      const deletedModels = deleted.map(assessment => {
        const modelId = assessment.model_uuid || assessment.assessment_uuid;
        const assessmentName = assessment.assessment_name || assessment.model_name || 'Unnamed Assessment';
        const ownerName = assessment.owner?.name || assessment.owner_name || assessment.created_by_name;
        const ownerEmail = assessment.owner?.email || assessment.owner_email;
        const userAvatar = assessment.avatar || assessment.owner?.avatar;

        return {
          modelId: modelId,
          modelName: assessmentName,
          model_name: assessment.model_name,
          assessmentName: assessmentName,
          modelType: assessment.model_type,
          modelStage: assessment.model_stage,
          useCaseShortDescription: assessment.use_case_short_description,
          assessmentUuid: assessment.assessment_uuid,
          assessmentStatus: assessment.status || 'Not Started',
          position: assessment.position,
          step: assessment.current_step,
          mode: assessment.mode,
          template_id: assessment.template_id,
          backendProgress: assessment.progress,
          review_status: assessment.review_status,
          deleted_at: assessment.deleted_at,
          deleted_by: assessment.deleted_by,
          deleted_by_email: assessment.deleted_by_email,
          created_at: assessment.created_at,
          created_by: assessment.created_by,
          created_by_name: assessment.created_by_name,
          avatar: userAvatar,
          updated_at: assessment.updated_at,
          last_updated_by: assessment.last_updated_by,
          last_updated_by_name: assessment.last_updated_by_name,
          last_updated_by_email: assessment.last_updated_by_email,
          last_activity_at: assessment.last_activity_at,
          last_activity_by_name: assessment.last_activity_by_name,
          last_activity_by_email: assessment.last_activity_by_email,
          owner_id: assessment.owner?.id || assessment.owner_id,
          owner_name: ownerName,
          owner_email: ownerEmail,
          createdByName: ownerName,
          assessmentType: assessment.assessment_type || 'builder',
          progress: calculateProgress({ position: assessment.position, assessmentStatus: assessment.status }),
          lastActivity: assessment.updated_at || assessment.last_activity_at || assessment.created_at,
          lastActivityBy: assessment.last_updated_by_name || assessment.last_activity_by_name || assessment.owner_name || assessment.created_by_name || 'Unknown User'
        };
      });
      setDeletedAssessments(deletedModels);
    } catch (error) {
      console.error('[RiskAssessmentsMantine] Restore failed:', error);
    }
  };

  // NEW: Update loading state based on new Redux structure
  useEffect(() => {
    if (!assessmentsLoading && Object.keys(allAssessmentsById).length >= 0) {
      setIsLoading(false);
    }
  }, [assessmentsLoading, allAssessmentsById]);

  // Set smart default tab when data loads (only once, not when user manually selects)
  useEffect(() => {
    if (!isLoading && !userHasSelectedTab) {
      const defaultTab = getDefaultTab;
      console.log('Setting smart default tab to:', defaultTab);
      setActiveTab(defaultTab);
    }
  }, [isLoading, getDefaultTab, userHasSelectedTab]);

  // Fetch deleted assessments on mount to show correct count
  // IMPORTANT: Fetch directly from API without updating Redux to avoid polluting main state
  useEffect(() => {
    const fetchDeletedAssessments = async () => {
      if (isAuthenticated) {
        try {
          // Get auth token
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
              scope: 'openid profile email read:models write:models delete:models'
            }
          });

          // Fetch directly from API without dispatching to Redux
          const response = await authFetch('/.netlify/functions/getAssessments?includeDeleted=true', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to fetch deleted assessments`);
          }

          const result = await response.json();
          if (!result.success) {
            throw new Error(result.error || 'Failed to fetch deleted assessments');
          }

          // Filter only deleted assessments (have deleted_at timestamp)
          const deleted = result.assessments.filter(a => a.deleted_at);

          // Transform to model format
          const deletedModels = deleted.map(assessment => {
            const modelId = assessment.model_uuid || assessment.assessment_uuid;
            const assessmentName = assessment.assessment_name || assessment.model_name || 'Unnamed Assessment';
            const ownerName = assessment.owner?.name || assessment.owner_name || assessment.created_by_name;
            const ownerEmail = assessment.owner?.email || assessment.owner_email;
            const userAvatar = assessment.avatar || assessment.owner?.avatar;

            return {
              modelId: modelId,
              modelName: assessmentName,
              model_name: assessment.model_name,
              assessmentName: assessmentName,
              modelType: assessment.model_type,
              modelStage: assessment.model_stage,
              useCaseShortDescription: assessment.use_case_short_description,
              assessmentUuid: assessment.assessment_uuid,
              assessmentStatus: assessment.status || 'Not Started',
              position: assessment.position,
              step: assessment.current_step,
              mode: assessment.mode,
              template_id: assessment.template_id,
              backendProgress: assessment.progress,
              review_status: assessment.review_status,
              deleted_at: assessment.deleted_at,
              deleted_by: assessment.deleted_by,
              deleted_by_email: assessment.deleted_by_email,
              created_at: assessment.created_at,
              created_by: assessment.created_by,
              created_by_name: assessment.created_by_name,
              avatar: userAvatar,
              updated_at: assessment.updated_at,
              last_updated_by: assessment.last_updated_by,
              last_updated_by_name: assessment.last_updated_by_name,
              last_updated_by_email: assessment.last_updated_by_email,
              last_activity_at: assessment.last_activity_at,
              last_activity_by_name: assessment.last_activity_by_name,
              last_activity_by_email: assessment.last_activity_by_email,
              owner_id: assessment.owner?.id || assessment.owner_id,
              owner_name: ownerName,
              owner_email: ownerEmail,
              createdByName: ownerName,
              assessmentType: assessment.assessment_type || 'builder',
              progress: calculateProgress({ position: assessment.position, assessmentStatus: assessment.status }),
              lastActivity: assessment.updated_at || assessment.last_activity_at || assessment.created_at,
              lastActivityBy: assessment.last_updated_by_name || assessment.last_activity_by_name || assessment.owner_name || assessment.created_by_name || 'Unknown User'
            };
          });

          setDeletedAssessments(deletedModels);
        } catch (error) {
          console.error('[RiskAssessmentsMantine] Failed to fetch deleted assessments:', error);
        }
      }
    };

    fetchDeletedAssessments();
  }, [isAuthenticated, getAccessTokenSilently, calculateProgress]);

  // Show loading screen with AI robot while loading OR if not authenticated yet
  if (isLoading || !isAuthenticated) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="xl">
          <AIGeneratingIcon isGenerating size={64} color="#228be6" />
          <Text size="lg" c="dimmed" fw={500}>
            Loading assessments...
          </Text>
        </Stack>
      </Center>
    );
  }

  // If an assessment is selected for review, render ReviewMode instead of the table
  if (selectedAssessmentForReview) {
    return (
      <ReviewMode
        modelId={selectedAssessmentForReview.modelId}
        assessmentUuid={selectedAssessmentForReview.assessmentUuid}
      />
    );
  }

  return (
    <Container size="xl" px="md">
      <Stack spacing="xl">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <IconProgressCheck size={24} stroke={1.5} color="var(--mantine-color-dimmed)" />
            <Title order={2} fw={500}>Risk Assessments</Title>
          </Group>
          <Button
            leftSection={<IconRocket size={18} />}
            size="md"
            radius="md"
            onClick={() => setShowModeDialog(true)}
          >
            Start Assessment
          </Button>
        </Group>

        {/* Chip Filters */}
        <Stack gap="sm">
          {/* Status Filter */}
          <Group gap="xs">
            <Text size="sm" c="dimmed" fw={500} w={50}>Status:</Text>
            <Chip.Group
              value={activeTab}
              onChange={(value) => {
                console.log('Status chip clicked:', value);
                setUserHasSelectedTab(true);
                setActiveTab(value);
              }}
            >
              <Group gap="xs">
                <Chip value="all" variant="light" size="sm" radius="md">
                  All ({statusCounts.all})
                </Chip>
                <Chip value="ongoing" variant="light" size="sm" radius="md" color="blue">
                  On-Going ({statusCounts.ongoing})
                </Chip>
                <Chip value="completed" variant="light" size="sm" radius="md" color="green">
                  Completed ({statusCounts.completed})
                </Chip>
                {/* Risk Manager: For Review chip */}
                {userIsRiskManager && statusCounts.forreview > 0 && (
                  <Chip value="forreview" variant="light" size="sm" radius="md" color="orange">
                    For Review ({statusCounts.forreview})
                  </Chip>
                )}
                {/* Regular User: In Review chip */}
                {!userIsRiskManager && statusCounts.inreview > 0 && (
                  <Chip value="inreview" variant="light" size="sm" radius="md" color="orange">
                    In Review ({statusCounts.inreview})
                  </Chip>
                )}
                {/* Review Completed chip */}
                {statusCounts.reviewcompleted > 0 && (
                  <Chip value="reviewcompleted" variant="light" size="sm" radius="md" color="teal">
                    Review Done ({statusCounts.reviewcompleted})
                  </Chip>
                )}
                {/* Archive chip - for owners and Risk Managers */}
                <Chip value="deleted" variant="light" size="sm" radius="md" color="gray">
                  Archive ({statusCounts.deleted})
                </Chip>
              </Group>
            </Chip.Group>
          </Group>

          {/* Type Filter */}
          <Group gap="xs">
            <Text size="sm" c="dimmed" fw={500} w={50}>Type:</Text>
            <Chip.Group
              value={typeFilter}
              onChange={(value) => {
                console.log('Type chip clicked:', value);
                setTypeFilter(value);
              }}
            >
              <Group gap="xs">
                <Chip value="all" variant="light" size="sm" radius="md">
                  All
                </Chip>
                <Chip value="builder" variant="light" size="sm" radius="md" color="blue">
                  Register Builder ({statusCounts.builder})
                </Chip>
                <Chip value="use_case" variant="light" size="sm" radius="md" color="orange">
                  Builder ({statusCounts.use_case})
                </Chip>
                <Chip value="business_case" variant="light" size="sm" radius="md" color="green">
                  Explorer ({statusCounts.business_case})
                </Chip>
              </Group>
            </Chip.Group>
          </Group>
        </Stack>

        <Divider />

        {/* Search and Sort */}
        <Group spacing="md">
          <TextInput
            placeholder="Search assessments..."
            leftSection={<IconSearch size={18} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1 }}
            size="md"
            radius="md"
          />
          <Select
            placeholder="Sort by"
            value={sortBy}
            onChange={setSortBy}
            data={[
              { value: 'updated_at', label: 'Last Updated' },
              { value: 'created_at', label: 'Date Created' },
              { value: 'name', label: 'Assessment Name' },
              { value: 'type', label: 'Type' },
              { value: 'progress', label: 'Progress' }
            ]}
            w={180}
            size="md"
            radius="md"
          />
        </Group>

        {/* Enhanced Table */}
        <Card shadow="sm" padding={0} radius="md" withBorder>
          <Table highlightOnHover verticalSpacing="md" fontSize="sm">
            <thead style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
              <tr>
                <th style={{ padding: '16px 20px', fontWeight: 600, textAlign: 'left', minWidth: '180px' }}>Assessment</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left', minWidth: '120px' }}>Model</th>
                <th style={{ width: 90, textAlign: 'center', padding: '16px 12px', fontWeight: 600 }}>Progress</th>
                <th style={{ width: 90, padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Type</th>
                <th style={{ width: 120, padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Risks</th>
                <th style={{ width: 130, padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Review</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left', minWidth: '120px' }}>Phase</th>
                <th style={{ width: 100, padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Updated</th>
                <th style={{ width: 100, textAlign: 'center', padding: '16px 12px', fontWeight: 600 }}>Owner</th>
                <th style={{ width: 90, textAlign: 'center', padding: '16px 12px', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredModels.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <Center py={60}>
                      <Stack align="center" spacing="md">
                        <ThemeIcon size={64} radius="xl" variant="light" color="gray">
                          <IconAlertCircle size={32} />
                        </ThemeIcon>
                        <Text size="lg" weight={500} c="dimmed">
                          No assessments found
                        </Text>
                        <Text size="sm" c="dimmed">
                          {searchQuery 
                            ? `No results for "${searchQuery}"` 
                            : activeTab === 'all' 
                              ? 'No assessments available' 
                              : `No ${activeTab === 'ongoing' ? 'on-going' : activeTab === 'notstarted' ? 'not started' : 'completed'} assessments`
                          }
                        </Text>
                      </Stack>
                    </Center>
                  </td>
                </tr>
              ) : (
                filteredModels.map((model) => (
                  <React.Fragment key={model.modelId}>
                    <tr>
                      <td style={{ padding: '16px 20px', textAlign: 'left', maxWidth: '200px' }}>
                        <Tooltip label={model.assessmentName || model.modelName || 'Unnamed Assessment'} withArrow disabled={(model.assessmentName || model.modelName || '').length <= 30}>
                          <Text
                            weight={600}
                            size="sm"
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '180px'
                            }}
                          >
                            {model.assessmentName || model.modelName || 'Unnamed Assessment'}
                          </Text>
                        </Tooltip>
                      </td>

                      <td style={{ padding: '16px 12px', textAlign: 'left', maxWidth: '150px' }}>
                        <Tooltip label={model.model_name || 'No model'} withArrow disabled={!model.model_name || model.model_name.length <= 25}>
                          <Text
                            size="sm"
                            c="dimmed"
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '130px'
                            }}
                          >
                            {model.model_name || '—'}
                          </Text>
                        </Tooltip>
                      </td>

                      <td style={{ textAlign: 'center', padding: '16px 12px' }}>
                        <Center>
                          <ProgressRing progress={model.progress} size={45} />
                        </Center>
                      </td>
                      
                      <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                        <TypeBadge type={model.assessmentType} />
                      </td>

                      <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                        <RiskSummary riskStats={model.riskStats} />
                      </td>

                      <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                        <Tooltip
                          label={
                            model.reviewed_by_name
                              ? `Reviewed by ${model.reviewed_by_name}${model.reviewed_at ? ` on ${new Date(model.reviewed_at).toLocaleDateString()}` : ''}`
                              : model.review_started_by_name
                              ? `Review started by ${model.review_started_by_name}${model.review_started_at ? ` on ${new Date(model.review_started_at).toLocaleDateString()}` : ''}`
                              : null
                          }
                          withArrow
                          disabled={!model.reviewed_by_name && !model.review_started_by_name}
                        >
                          <div>
                            <ReviewStatusBadge reviewStatus={model.review_status} />
                            {(model.reviewed_by_name || model.review_started_by_name) && (
                              <Text size="xs" c="dimmed" mt={4}>
                                {model.reviewed_by_name || model.review_started_by_name}
                              </Text>
                            )}
                          </div>
                        </Tooltip>
                      </td>
                      
                      <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                        <Text size="sm" c="dimmed">
                          {model.assessmentStatus === 'Completed' ? 'Completed' : getPositionLabel(model.position, model.step)}
                        </Text>
                      </td>
                      
                      <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                        <Text size="sm" c="dimmed">
                          {getTimeSinceActivity(model.lastActivity)}
                        </Text>
                      </td>
                      
                      <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                        <Center>
                          <Group gap={4}>
                            {/* Owner avatar */}
                            <Tooltip
                              label={
                                <Stack gap={2}>
                                  <Text size="xs" fw={600}>Owner</Text>
                                  <Text size="xs">{model.owner_name || model.createdByName || 'Unknown'}</Text>
                                </Stack>
                              }
                              withArrow
                            >
                              <Avatar
                                size={26}
                                color="blue"
                                radius="xl"
                                src={model.avatar}
                                style={{ border: '2px solid var(--mantine-color-blue-3)' }}
                              >
                                <IconUser size={14} />
                              </Avatar>
                            </Tooltip>

                            {/* Reviewer avatar (if review in progress or completed) */}
                            {(model.reviewed_by_name || model.review_started_by_name) && (
                              <Tooltip
                                label={
                                  <Stack gap={2}>
                                    <Text size="xs" fw={600}>
                                      {model.reviewed_by_name ? 'Reviewer' : 'Review Started By'}
                                    </Text>
                                    <Text size="xs">{model.reviewed_by_name || model.review_started_by_name}</Text>
                                  </Stack>
                                }
                                withArrow
                              >
                                <Avatar
                                  size={26}
                                  color={model.reviewed_by_name ? 'green' : 'orange'}
                                  radius="xl"
                                  style={{ marginLeft: -8, border: `2px solid var(--mantine-color-${model.reviewed_by_name ? 'green' : 'orange'}-3)` }}
                                >
                                  <IconClipboardCheck size={14} />
                                </Avatar>
                              </Tooltip>
                            )}
                          </Group>
                        </Center>
                      </td>
                      
                      <td style={{ textAlign: 'center', padding: '16px 12px' }}>
                        <Center>
                          <Group gap="xs">
                          {/* Deleted assessments: Show restore button */}
                          {activeTab === 'deleted' ? (
                            <Tooltip label="Restore Assessment" withArrow>
                              <ActionIcon
                                variant="light"
                                color="blue"
                                size="lg"
                                radius="md"
                                onClick={() => handleRestoreClick(model.assessmentUuid)}
                              >
                                <IconRestore size={18} />
                              </ActionIcon>
                            </Tooltip>
                          ) : model.assessmentStatus !== 'Completed' ? (
                            // Non-completed assessments: Check permissions before showing action button
                            canShowActionButton(model) ? (
                              (() => {
                                const buttonConfig = getActionButtonConfig(model);
                                return (
                                  <Tooltip label={buttonConfig.tooltip} withArrow>
                                    <ActionIcon
                                      variant="light"
                                      color={buttonConfig.color}
                                      size="lg"
                                      radius="md"
                                      onClick={() => handleModelSelection(model)}
                                    >
                                      <IconPlayerPlay size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                );
                              })()
                            ) : (
                              // Co-pilot/legacy assessment but user is not Risk Manager - show disabled view button
                              <Tooltip label="Contact Risk Manager to access this assessment" withArrow>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  size="lg"
                                  radius="md"
                                  disabled
                                >
                                  <IconEye size={18} />
                                </ActionIcon>
                              </Tooltip>
                            )
                          ) : (
                            // Completed assessments: Different behavior for Risk Managers vs Regular Users
                            userIsRiskManager && model.review_status === 'submitted' ? (
                              <Tooltip label="Review Assessment" withArrow>
                                <ActionIcon
                                  variant="light"
                                  color="orange"
                                  size="lg"
                                  radius="md"
                                  onClick={() => setSelectedAssessmentForReview({
                                    modelId: model.modelId,
                                    assessmentUuid: model.assessmentUuid
                                  })}
                                >
                                  <IconClipboardCheck size={18} />
                                </ActionIcon>
                              </Tooltip>
                            ) : (
                              <Tooltip label="View Report" withArrow>
                                <ActionIcon
                                  variant="light"
                                  color="green"
                                  size="lg"
                                  radius="md"
                                  onClick={() => {
                                    // Route based on assessment type
                                    if (model.assessmentType === 'use_case') {
                                      navigate(`/builder-report/${model.modelId}/${model.assessmentUuid}`);
                                    } else if (model.assessmentType === 'business_case' || model.assessmentType === 'explorer') {
                                      navigate(`/explorer-report/${model.modelId}/${model.assessmentUuid}`);
                                    } else {
                                      navigate(`/assessment-report/${model.modelId}`);
                                    }
                                  }}
                                >
                                  <IconFileAnalytics size={18} />
                                </ActionIcon>
                              </Tooltip>
                            )
                          )}

                          {/* Delete button - show for owner or Risk Manager */}
                          {(model.owner?.id === currentUserId || userIsRiskManager) && (
                            <Tooltip label="Delete Assessment" withArrow>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                size="lg"
                                radius="md"
                                onClick={() => handleDeleteClick(model)}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          </Group>
                        </Center>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        </Card>
      </Stack>

      {/* Mode Selection Dialog for New Assessments */}
      <StartAssessmentDialogV2
        open={showModeDialog}
        onClose={() => {
          setShowModeDialog(false);
          setSelectedModelForAssessment(null);
        }}
        preselectedModel={selectedModelForAssessment}
      />

      {/* Review Mode */}
      {selectedAssessmentForReview && (
        <ReviewMode
          modelId={selectedAssessmentForReview.modelId}
          assessmentUuid={selectedAssessmentForReview.assessmentUuid}
          isOpen={!!selectedAssessmentForReview}
          onClose={() => setSelectedAssessmentForReview(null)}
        />
      )}

      {/* Delete Assessment Modal */}
      <DeleteAssessmentModal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setAssessmentToDelete(null);
        }}
        assessment={assessmentToDelete}
        userIsRiskManager={userIsRiskManager}
        dependentAssessments={[]} // TODO: Fetch dependent assessments
        onDelete={handleDeleteConfirm}
      />
    </Container>
  );
};

export default RiskAssessmentsMantine;
